import { useState } from "react";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { Input } from "@volca/ui/components/ui/input";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { CheckRequest } from "@/profitability/settings/types";
import { AskCheck } from "./ask-check";
import { ReorderableRules } from "./reorderable-rules";
import { RulePeriodSelect, useRulePeriod } from "./rule-period";
import { SettingsGroup, SettingsRow } from "./settings-group";
import { useRuleOrder } from "./use-rule-order";
import type { TemplateSettingsState } from "./use-settings-state";

/** A check asked for that the template's files cannot show yet. */
const RequestRow = ({
  request,
  editable,
  onDismiss,
}: {
  request: CheckRequest;
  editable: boolean;
  onDismiss: () => void;
}) => (
  <SettingsRow
    label={`“${request.request}”`}
    value={
      <Stack direction="row" gap={2} align="center" wrap>
        <Badge variant="outline" size="sm">
          Not checked yet
        </Badge>
        <Text variant="caption" tone="muted" className="text-pretty">
          Needs {request.missing}
        </Text>
      </Stack>
    }
    action={
      editable && (
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Forget “${request.request}”`}
          onClick={onDismiss}
        >
          Forget
        </Button>
      )
    }
  />
);

/** Compact navigation to each rule’s condition, findings and history. */
export const ReviewRulesGroup = ({
  reportId,
  state,
  canAsk,
}: {
  reportId: string;
  state: TemplateSettingsState;
  /** Whether the person may ask for a check (they manage the template). */
  canAsk: boolean;
}) => {
  const [search, setSearch] = useState("");
  const { settings, editable, save } = state;
  const rules = settings?.rules ?? [];
  const requests = settings?.requests ?? [];
  const periods = settings?.periods ?? [];
  const period = useRulePeriod(periods);
  const order = useRuleOrder(state);
  return (
    <SettingsGroup title="Rules">
      {rules.length > 0 && (
        <Stack gap={3} className="p-4 sm:flex-row sm:items-center">
          <Input
            aria-label="Find a review rule"
            placeholder="Find a rule…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <RulePeriodSelect
            periods={periods}
            runId={period.runId}
            onChange={period.select}
          />
          {editable && settings?.ordered && (
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 self-start sm:self-auto"
              disabled={order.busy}
              onClick={() => void order.saveOrder(null)}
            >
              Reset order
            </Button>
          )}
        </Stack>
      )}
      {!settings ? (
        <div className="p-4">
          <Skeleton
            role="status"
            aria-label="Loading rules"
            className="h-16 w-full"
          />
        </div>
      ) : rules.length === 0 ? (
        <SettingsRow
          label="No rules yet"
          hint="Ask Volca to add a check based on the data in your source files."
          action={
            editable &&
            settings.ordered && (
              <Button
                variant="ghost"
                size="sm"
                disabled={order.busy}
                onClick={() => void order.saveOrder(null)}
              >
                Reset order
              </Button>
            )
          }
        />
      ) : (
        <ReorderableRules
          reportId={reportId}
          runId={period.runId}
          state={state}
          order={order}
          search={search}
          onClearSearch={() => setSearch("")}
        />
      )}
      {requests.map((request) => (
        <RequestRow
          key={request.id}
          request={request}
          editable={editable}
          onDismiss={() => {
            void save({ dismiss_request: request.id }).catch(() => undefined);
          }}
        />
      ))}
      {canAsk && settings && <AskCheck reportId={reportId} />}
    </SettingsGroup>
  );
};
